/**
 * useTouchDragAttack - Hook for mobile touch drag-to-attack
 * 
 * Allows users to touch a unit in their zone and drag to an enemy unit
 * or the opponent's general to initiate an attack.
 * 
 * Features:
 * - Touch drag from friendly zone units
 * - Visual feedback with attack line from attacker to finger
 * - Target highlighting for valid targets
 * - Hit detection on enemy units and general
 * - Works alongside existing click-click attack mode
 */

import { useState, useCallback, useRef } from 'react';

const useTouchDragAttack = ({
  isMobile,
  yourTurn,
  gameState,
  userId,
}) => {
  // State for tracking attack drag
  const [isDragging, setIsDragging] = useState(false);
  const [attackerInfo, setAttackerInfo] = useState(null); // { unit, zone, index }
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [hoveredTarget, setHoveredTarget] = useState(null); // { type: 'unit'|'general', zone?, index?, element? }
  
  // Refs for tracking touch state
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const hasDraggedRef = useRef(false);
  const attackerRef = useRef(null);
  
  // Threshold for considering it a drag vs tap (in pixels)
  const DRAG_THRESHOLD = 20;
  
  /**
   * Find what element is under the touch point
   * Returns target info if it's a valid attack target
   */
  const findTargetAtPosition = useCallback((x, y) => {
    // Get all elements at position (even those with pointerEvents: none)
    const elementsAtPoint = document.elementsFromPoint(x, y);
    
    for (const element of elementsAtPoint) {
      // Check for opponent general - multiple ways to detect
      const generalMatch = element.closest('[data-target="opponent-general"]') || 
          element.closest('.opponent-general') ||
          element.closest('.opponent-general-avatar') ||
          element.closest('[data-general="opponent"]') ||
          element.closest('[data-zone="opponent_general"]') ||
          element.closest('.opponent-health-orb-container') ||
          element.closest('[data-target="opponent-general-area"]');
      
      if (generalMatch ||
          element.getAttribute?.('data-zone') === 'opponent_general' ||
          element.getAttribute?.('data-target') === 'opponent-general' ||
          element.getAttribute?.('alt') === 'Opponent General') {
        console.log('⚔️ HIT DETECTION: Found opponent general via:', generalMatch?.className || element.getAttribute?.('data-target') || element.getAttribute?.('alt'));
        return { type: 'general' };
      }
      
      // Check for opponent unit in zone
      const slotElement = element.closest('[data-slot-zone][data-slot-index]');
      if (slotElement) {
        const zone = slotElement.getAttribute('data-slot-zone');
        const index = parseInt(slotElement.getAttribute('data-slot-index'), 10);
        
        // Only target opponent zones
        if (zone && zone.startsWith('opponent_')) {
          console.log('⚔️ HIT DETECTION: Found opponent unit at', zone, index);
          return { type: 'unit', zone, index, element: slotElement };
        }
      }
      
      // Alternative: check for data attributes on order design elements
      const orderElement = element.closest('[data-unit-zone][data-unit-index]');
      if (orderElement) {
        const zone = orderElement.getAttribute('data-unit-zone');
        const index = parseInt(orderElement.getAttribute('data-unit-index'), 10);
        
        if (zone && zone.startsWith('opponent_')) {
          console.log('⚔️ HIT DETECTION: Found opponent unit (alt) at', zone, index);
          return { type: 'unit', zone, index, element: orderElement };
        }
      }
    }
    
    return null;
  }, []);
  
  /**
   * Handle touch start on a zone unit
   */
  const handleTouchStart = useCallback((e, unit, zone, index) => {
    if (!isMobile || !yourTurn) return false;
    
    // Only allow attacking with friendly units (not opponent_ zones)
    if (zone.startsWith('opponent_')) return false;
    
    // Check if unit can attack (has attack rating > 0)
    const attackRating = unit?.attack_rating ?? 0;
    if (attackRating === 0) return false;
    
    // Check if unit has already attacked this turn
    if (unit?.hasAttacked) return false;
    
    // Check if unit is sleeping (zZz)
    if (unit?.isSleeping) return false;
    
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    hasDraggedRef.current = false;
    attackerRef.current = { unit, zone, index };
    
    setStartPosition({ x: touch.clientX, y: touch.clientY });
    
    console.log('⚔️ ATTACK TOUCH START:', { unitName: unit?.name, zone, index });
    return true;
  }, [isMobile, yourTurn]);
  
  /**
   * Handle touch move during attack drag
   */
  const handleTouchMove = useCallback((e) => {
    if (!isMobile || !yourTurn || !attackerRef.current) return;
    
    const touch = e.touches[0];
    if (!touch) return;
    
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Check if we've moved enough to consider this a drag
    if (!hasDraggedRef.current && distance > DRAG_THRESHOLD) {
      hasDraggedRef.current = true;
      setIsDragging(true);
      setAttackerInfo(attackerRef.current);
      console.log('⚔️ ATTACK DRAG STARTED for:', attackerRef.current?.unit?.name);
    }
    
    if (hasDraggedRef.current) {
      // Update drag position
      setDragPosition({
        x: touch.clientX,
        y: touch.clientY
      });
      
      // Find what's under the finger
      const target = findTargetAtPosition(touch.clientX, touch.clientY);
      setHoveredTarget(target);
    }
  }, [isMobile, yourTurn, findTargetAtPosition]);
  
  /**
   * Handle touch end - returns attack info if valid target
   * @returns {{ wasAttackDrag: boolean, attacker: object|null, target: object|null }}
   */
  const handleTouchEnd = useCallback((e) => {
    if (!isMobile || !yourTurn) {
      return { wasAttackDrag: false, attacker: null, target: null };
    }
    
    const attacker = attackerRef.current;
    const wasAttackDrag = hasDraggedRef.current && isDragging;
    const target = hoveredTarget;
    
    // Reset refs
    hasDraggedRef.current = false;
    attackerRef.current = null;
    
    // Reset state
    setIsDragging(false);
    setAttackerInfo(null);
    setDragPosition({ x: 0, y: 0 });
    setStartPosition({ x: 0, y: 0 });
    setHoveredTarget(null);
    
    if (wasAttackDrag && attacker && target) {
      console.log('⚔️ ATTACK DRAG END:', { 
        attacker: attacker.unit?.name, 
        targetType: target.type,
        targetZone: target.zone,
        targetIndex: target.index 
      });
      e.preventDefault();
      return { wasAttackDrag: true, attacker, target };
    }
    
    return { wasAttackDrag, attacker, target: null };
  }, [isMobile, yourTurn, isDragging, hoveredTarget]);
  
  /**
   * Cancel attack drag
   */
  const cancelDrag = useCallback(() => {
    hasDraggedRef.current = false;
    attackerRef.current = null;
    setIsDragging(false);
    setAttackerInfo(null);
    setDragPosition({ x: 0, y: 0 });
    setStartPosition({ x: 0, y: 0 });
    setHoveredTarget(null);
  }, []);
  
  return {
    // State
    isDragging,
    attackerInfo,
    dragPosition,
    startPosition,
    hoveredTarget,
    
    // Handlers
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    cancelDrag,
  };
};

export default useTouchDragAttack;

